import { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Link,
  Image,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuGroup,
  MenuDivider,
  Text,
  Popover,
  PopoverTrigger,
  PopoverContent
} from '@chakra-ui/react';
import { useAuth } from '../../context/AuthContext';
import { useMainMenu } from '../../config/menuConfig';
import i18n from '../../config/i18n'; // Ajusta: la ruta si es necesario

const Header = () => {
  const { isAuthenticated, user, logout } = useAuth();

  // Estado para el idioma seleccionado - inicializar con un valor por defecto
  const [language, setLanguage] = useState(() => {
    // Prioridad: idioma del usuario > localStorage > idioma por defecto
    if (user?.idioma) {
      return user.idioma;
    }
    const storedLang = localStorage.getItem('appLanguage');
    return storedLang || 'es'; // 'es' como valor por defecto
  });

  // Sincronizar idioma cuando el usuario cambia o se autentica
  useEffect(() => {
    if (user?.idioma) {
      setLanguage(user.idioma);
      i18n.changeLanguage(user.idioma);
      localStorage.setItem('appLanguage', user.idioma);
    } else {
      // Si no hay usuario, usar el idioma del localStorage o por defecto
      const storedLang = localStorage.getItem('appLanguage') || 'es';
      setLanguage(storedLang);
      i18n.changeLanguage(storedLang);
    }
  }, [user]);

  const handleLogout = async () => {
    try {
      await fetch('/api/sesiones/logout/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (e) {
      // Puedes mostrar un toast o ignorar el error
    }
    logout();
    window.location.href = '/login';
  };

  // Cambiar idioma
  const handleChangeLanguage = (lang) => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem('appLanguage', lang); // Guarda el idioma
  };

  const iconosDisponibles = ['es', 'ca', 'en', 'de', 'fr', 'it', 'ru', 'zh'];
  const currentIconLanguage = iconosDisponibles.includes(language) ? language : 'es';
  const flagStyle = {
    height: 18,
    borderRadius: 4,
    background: 'transparent',
    display: 'block',
    boxShadow: 'none',
    filter: 'none'
  };

  // Forzar re-render al cambiar el idioma
  useEffect(() => {
    const onLangChanged = () => setLanguage(i18n.language);
    i18n.on('languageChanged', onLangChanged);
    return () => i18n.off('languageChanged', onLangChanged);
  }, []);

  // Obtén el menú según el usuario
  const menu = useMainMenu({ isAuthenticated, user });

  return (
    <Box
      color="white"
      minH="64px"
      px={4}
      backgroundImage="linear-gradient(to bottom, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0)), url('/chat_pattern.png')"
      backgroundRepeat="repeat"
      backgroundSize="auto"
      backgroundPosition="top left"
    >
      <Flex align="center" justify="space-between" h="64px">
        {/* Menú hamburguesa + nombre usuario + icono persona a la izquierda */}
        <Flex align="center" height="100%">
          <Menu>
            <MenuButton
              as={IconButton}
              icon={
                <svg width="24" height="24" fill="white" viewBox="0 0 24 24">
                  <rect y="4" width="24" height="2" rx="1"/>
                  <rect y="11" width="24" height="2" rx="1"/>
                  <rect y="18" width="24" height="2" rx="1"/>
                </svg>
              }
              variant="outline"
              colorScheme="whiteAlpha"
              color="white"
              aria-label="Abrir menú"
              mr={2}
            />
            <MenuList color="black" bg="white" zIndex={2000} boxShadow="8px 8px 16px rgba(0, 0, 0, 0.3)">
              {menu.map((item, idx) =>
                item.submenu ? (
                  <MenuGroup title={item.title} key={item.title}>
                    {item.submenu.map((sub, subIdx) => 
                      sub.submenu ? (
                        // Submenú anidado con Popover en hover
                        <Popover 
                          key={subIdx} 
                          trigger="hover" 
                          placement="right-start" 
                          openDelay={50}
                          closeDelay={500}
                          gutter={0}
                          offset={[0, 0]}
                        >
                          {({ isOpen }) => (
                            <>
                              <PopoverTrigger>
                                <MenuItem
                                  bg="white"
                                  _hover={{ bg: 'gray.100' }}
                                  cursor="pointer"
                                >
                                  {sub.title} ▸
                                </MenuItem>
                              </PopoverTrigger>
                              <PopoverContent 
                                w="auto" 
                                minW="150px" 
                                bg="white" 
                                border="1px" 
                                borderColor="gray.200" 
                                boxShadow="8px 8px 16px rgba(0, 0, 0, 0.3)"
                                _focus={{ outline: 'none' }}
                                ml="-1px"
                                zIndex={2001}
                              >
                                <Box py={1}>
                                  {sub.submenu.map((nestedItem, nestedIdx) => (
                                    <MenuItem
                                      as="a"
                                      href={nestedItem.path}
                                      key={nestedIdx}
                                      bg="white"
                                      _hover={{ bg: 'gray.100' }}
                                      fontSize="sm"
                                    >
                                      {nestedItem.title}
                                    </MenuItem>
                                  ))}
                                </Box>
                              </PopoverContent>
                            </>
                          )}
                        </Popover>
                      ) : (
                        // Item normal del submenú
                        <MenuItem
                          as="a"
                          href={sub.path}
                          key={subIdx}
                          bg="white"
                          _hover={{ bg: 'gray.100' }}
                        >
                          {sub.title}
                        </MenuItem>
                      )
                    )}
                    <MenuDivider />
                  </MenuGroup>
                ) : (
                  <MenuItem
                    as="a"
                    href={item.path}
                    key={item.title || idx}
                    bg="white"
                    _hover={{ bg: 'gray.100' }} // Fondo gris claro al hacer hover
                  >
                    {item.title}
                  </MenuItem>
                )
              )}
            </MenuList>
          </Menu>
          {isAuthenticated && user && (
            <Text fontSize="sm" fontWeight="medium" mr={2} whiteSpace="nowrap">
              {user.nombre} {user.apellidos}
            </Text>
          )}
          {isAuthenticated ? (
            <Link as="button" onClick={handleLogout} fontSize="2xl" aria-label="Logout" ml={2}>
              <span role="img" aria-label="logout">↩️</span>
            </Link>
          ) : (
            <Link href="/login" fontSize="2xl" aria-label="Login" ml={2}>
              <span role="img" aria-label="persona">🧑‍🤝‍🧑</span>
            </Link>
          )}
        </Flex>

        {/* Logo y selector de idioma juntos a la derecha */}
        <Flex align="center" height="100%" gap={4}>
          <Link href="/login" display="inline-flex" alignItems="center" aria-label="Inicio">
            <Image
              src="/assets/images/logo.svg"
              alt="Logo"
              boxSize="120px"
              objectFit="contain"
              maxH="64px"
            />
          </Link>
          <Menu>
            <MenuButton
              as={IconButton}
              aria-label="Seleccionar idioma"
              icon={
                <img
                  src={`/assets/icons/${currentIconLanguage}.png`}
                  alt={language}
                  className="lang-flag-icon"
                  style={flagStyle}
                />
              }
              className="lang-flag-button"
              variant="outline"
              bg="gray.200"
              _hover={{ bg: "gray.300" }}
              boxShadow="none"
              _focus={{ boxShadow: 'none' }}
              _active={{ boxShadow: 'none' }}
              mr={2}
            />
            <MenuList
              minW="40px"
              w="40px"
              p={0}
              display="flex"
              flexDirection="column"
              alignItems="center"
              bg="gray.200"
              boxShadow="none"
              zIndex={3000}
            >
              <MenuItem
                onClick={() => handleChangeLanguage('es')}
                className="lang-flag-item"
                p={1}
                px={0}
                w="auto"
                minW="unset"
                maxW="32px"
                display="flex"
                justifyContent="center"
                bg="gray.200"
                _hover={{ bg: 'gray.300' }}
                boxShadow="none"
                _focus={{ boxShadow: 'none' }}
                _active={{ boxShadow: 'none' }}
              >
                <img src="/assets/icons/es.png" alt="es" className="lang-flag-icon" style={flagStyle} />
              </MenuItem>
              <MenuItem
                onClick={() => handleChangeLanguage('ca')}
                className="lang-flag-item"
                p={1}
                px={0}
                w="auto"
                minW="unset"
                maxW="32px"
                display="flex"
                justifyContent="center"
                bg="gray.200"
                _hover={{ bg:"gray.300" }}
                boxShadow="none"
                _focus={{ boxShadow: 'none' }}
                _active={{ boxShadow: 'none' }}
              >
                <img src="/assets/icons/ca.png" alt="ca" className="lang-flag-icon" style={flagStyle} />
              </MenuItem>
              <MenuItem
                onClick={() => handleChangeLanguage('en')}
                className="lang-flag-item"
                p={1}
                px={0}
                w="auto"
                minW="unset"
                maxW="32px"
                display="flex"
                justifyContent="center"
                bg="gray.200"
                _hover={{ bg:"gray.300" }}
                boxShadow="none"
                _focus={{ boxShadow: 'none' }}
                _active={{ boxShadow: 'none' }}
              >
                <img src="/assets/icons/en.png" alt="en" className="lang-flag-icon" style={flagStyle} />
              </MenuItem>
              <MenuItem
                onClick={() => handleChangeLanguage('de')}
                className="lang-flag-item"
                p={1}
                px={0}
                w="auto"
                minW="unset"
                maxW="32px"
                display="flex"
                justifyContent="center"
                bg="gray.200"
                _hover={{ bg:"gray.300" }}
                boxShadow="none"
                _focus={{ boxShadow: 'none' }}
                _active={{ boxShadow: 'none' }}
              >
                <img src="/assets/icons/de.png" alt="de" className="lang-flag-icon" style={flagStyle} />
              </MenuItem>
              <MenuItem
                onClick={() => handleChangeLanguage('fr')}
                className="lang-flag-item"
                p={1}
                px={0}
                w="auto"
                minW="unset"
                maxW="32px"
                display="flex"
                justifyContent="center"
                bg="gray.200"
                _hover={{ bg:"gray.300" }}
                boxShadow="none"
                _focus={{ boxShadow: 'none' }}
                _active={{ boxShadow: 'none' }}
              >
                <img src="/assets/icons/fr.png" alt="fr" className="lang-flag-icon" style={flagStyle} />
              </MenuItem>
              <MenuItem
                onClick={() => handleChangeLanguage('it')}
                className="lang-flag-item"
                p={1}
                px={0}
                w="auto"
                minW="unset"
                maxW="32px"
                display="flex"
                justifyContent="center"
                bg="gray.200"
                _hover={{ bg:"gray.300" }}
                boxShadow="none"
                _focus={{ boxShadow: 'none' }}
                _active={{ boxShadow: 'none' }}
              >
                <img src="/assets/icons/it.png" alt="it" className="lang-flag-icon" style={flagStyle} />
              </MenuItem>
              <MenuItem
                onClick={() => handleChangeLanguage('ru')}
                className="lang-flag-item"
                p={1}
                px={0}
                w="auto"
                minW="unset"
                maxW="32px"
                display="flex"
                justifyContent="center"
                bg="gray.200"
                _hover={{ bg:"gray.300" }}
                boxShadow="none"
                _focus={{ boxShadow: 'none' }}
                _active={{ boxShadow: 'none' }}
              >
                <img src="/assets/icons/ru.png" alt="ru" className="lang-flag-icon" style={flagStyle} />
              </MenuItem>
              <MenuItem
                onClick={() => handleChangeLanguage('zh')}
                className="lang-flag-item"
                p={1}
                px={0}
                w="auto"
                minW="unset"
                maxW="32px"
                display="flex"
                justifyContent="center"
                bg="gray.200"
                _hover={{ bg:"gray.300" }}
                boxShadow="none"
                _focus={{ boxShadow: 'none' }}
                _active={{ boxShadow: 'none' }}
              >
                <img src="/assets/icons/zh.png" alt="zh" className="lang-flag-icon" style={flagStyle} />
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </Flex>
    </Box>
  );
};

export default Header;
